import { AdminCategoriesSortField, AdminCategoriesSortOrder, AdminCategoriesTypeFilter } from '@@types/api.types';
import type { SortingState } from '@tanstack/react-table';
import { parseAsSortingState } from '@utils/sorting.util';
import { parseAsInteger, parseAsString, parseAsStringEnum, useQueryState } from 'nuqs';
import { useCallback, useMemo } from 'react';

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_SORTING: SortingState = [{ desc: false, id: 'category' }];

const COLUMN_ID_TO_SORT_FIELD: Record<string, AdminCategoriesSortField> = {
  category: AdminCategoriesSortField.HIERARCHY,
  name: AdminCategoriesSortField.NAME,
  parent: AdminCategoriesSortField.PARENT,
  status: AdminCategoriesSortField.STATUS,
};

type AdminCategoriesQueryParams = {
  limit: number;
  order: AdminCategoriesSortOrder;
  page: number;
  search?: string;
  sort: AdminCategoriesSortField;
  type: AdminCategoriesTypeFilter;
};

type UseAdminCategoriesQueryParamsReturn = {
  DEFAULT_SORTING: SortingState;
  handleLimitChange: (newLimit: number) => void;
  page: number;
  queryParams: AdminCategoriesQueryParams;
  search: string;
  selectedCategoryId: string | null;
  setCategoryType: (type: AdminCategoriesTypeFilter) => Promise<URLSearchParams>;
  setPage: (page: number) => Promise<URLSearchParams>;
  setSearch: (search: string) => Promise<URLSearchParams>;
  setSelectedCategoryId: (categoryId: string | null) => Promise<URLSearchParams>;
  setSorting: (sorting: SortingState) => Promise<URLSearchParams>;
  sortingState: SortingState;
  type: AdminCategoriesTypeFilter;
};

export const useAdminCategoriesQueryParams = (): UseAdminCategoriesQueryParamsReturn => {
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [limit, setLimit] = useQueryState('limit', parseAsInteger.withDefault(DEFAULT_PAGE_SIZE));
  const [search, setSearch] = useQueryState('search', parseAsString.withDefault(''));
  const [selectedCategoryId, setSelectedCategoryId] = useQueryState('categoryId', parseAsString);
  const [type, setCategoryType] = useQueryState(
    'type',
    parseAsStringEnum<AdminCategoriesTypeFilter>([
      AdminCategoriesTypeFilter.ALL,
      AdminCategoriesTypeFilter.MAIN,
      AdminCategoriesTypeFilter.SUB,
    ]).withDefault(AdminCategoriesTypeFilter.ALL),
  );
  const [sorting, setSorting] = useQueryState('sorting', parseAsSortingState.withDefault(DEFAULT_SORTING));

  const sortingState: SortingState = sorting ?? DEFAULT_SORTING;

  const columnId = sortingState[0]?.id || 'name';
  const sortField = COLUMN_ID_TO_SORT_FIELD[columnId] || AdminCategoriesSortField.NAME;
  const sortOrder = sortingState[0]?.desc ? AdminCategoriesSortOrder.DESC : AdminCategoriesSortOrder.ASC;

  const queryParams = useMemo(() => {
    return {
      limit: limit ?? DEFAULT_PAGE_SIZE,
      order: sortOrder,
      page: page ?? 1,
      search: search || undefined,
      sort: sortField,
      type: type ?? AdminCategoriesTypeFilter.ALL,
    };
  }, [limit, page, search, sortField, sortOrder, type]);

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      void setLimit(newLimit);
      void setPage(1);
    },
    [setLimit, setPage],
  );

  return {
    DEFAULT_SORTING,
    handleLimitChange,
    page: page ?? 1,
    queryParams,
    search: search ?? '',
    selectedCategoryId,
    setCategoryType,
    setPage,
    setSearch,
    setSelectedCategoryId,
    setSorting,
    sortingState,
    type: type ?? AdminCategoriesTypeFilter.ALL,
  };
};
